import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Building2, Users, Droplets, Coffee, Sofa, Plus, Minus, ChevronDown, ArrowRight, Home, Sparkles, Phone } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
import { ServiceNav } from '../components/ServiceNav';
const cardClass = 'bg-white rounded-2xl border border-gray-200 p-8 md:p-10 shadow-sm';
const stepHeadingClass = 'font-bebas text-2xl md:text-3xl text-[#1a2744] pb-3 border-b border-gray-200';
const labelClass = 'text-[#1a2744] font-medium';
const inputClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all placeholder-gray-400';
const selectClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all appearance-none cursor-pointer';
export function CommercialQuote() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Step 1
  const [primaryUse, setPrimaryUse] = useState('');
  const [sqFootage, setSqFootage] = useState('');
  const [ceilings, setCeilings] = useState('');
  const [persons, setPersons] = useState('');
  const [frequency, setFrequency] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('');
  const [covid, setCovid] = useState<boolean | null>(null);
  // Step 2
  const [spaces, setSpaces] = useState({
    offices: 0,
    conference: 0,
    bathrooms: 0,
    cafeterias: 0,
    lounging: 0
  });
  const updateSpace = (key: keyof typeof spaces, increment: boolean) => {
    setSpaces(prev => ({
      ...prev,
      [key]: Math.max(0, prev[key] + (increment ? 1 : -1))
    }));
  };
  // Step 3
  const [flooring, setFlooring] = useState({
    wood: '',
    cement: '',
    carpeting: '',
    ceramic: '',
    other: ''
  });
  // Step 4 & 5
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    unit: '',
    city: '',
    postalCode: ''
  });
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({
      ...contact,
      [e.target.name]: e.target.value
    });
  };
  const percentages = ['0%', '10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%'];
  const spaceTypes = [{
    key: 'offices' as const,
    label: 'Enclosed Office Spaces',
    icon: Building2
  }, {
    key: 'conference' as const,
    label: 'Conference or Meeting Rooms',
    icon: Users
  }, {
    key: 'bathrooms' as const,
    label: 'Men & Women Bathrooms',
    icon: Droplets
  }, {
    key: 'cafeterias' as const,
    label: 'Cafeterias & Dining Areas',
    icon: Coffee
  }, {
    key: 'lounging' as const,
    label: 'Lounging / Employee Areas',
    icon: Sofa
  }];
  const flooringTypes = [{
    key: 'wood' as const,
    label: 'Wood - Approximate % of the total floor space?'
  }, {
    key: 'cement' as const,
    label: 'Cement - Approximate % of the floor space?'
  }, {
    key: 'carpeting' as const,
    label: 'Carpeting - Approximate % of the floor space? (excludes area & moveable rugs)'
  }, {
    key: 'ceramic' as const,
    label: 'Ceramic/Marble/Finished stone - Approximate % of the floor space?'
  }];
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-916">
      <SEOHead title="Commercial Cleaning Quote | Smith & Jengs Cleaning Services" description="Request a tailored quote for commercial cleaning services in Bicester and Oxfordshire." canonical="https://smithandjengs.com/commercial-cleaning-quote" data-id="element-917" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Commercial Cleaning',
      url: 'https://smithandjengs.com/commercial-cleaning'
    }, {
      name: 'Quote',
      url: 'https://smithandjengs.com/commercial-cleaning-quote'
    }]} data-id="element-918" />
      <Navbar data-id="element-919" />

      <main className="flex-1 pt-24" data-id="element-920">
        <ServiceNav data-id="element-921" />

        {/* Header */}
        <section className="w-full bg-white pt-12 md:pt-16 pb-12 px-4 md:px-12" data-id="element-922">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl w-full mx-auto text-center" data-id="element-923">
            <h1 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide mb-6" data-id="element-924">
              <span className="text-5xl md:text-6xl lg:text-7xl block" data-id="element-925">
                REQUEST A QUOTE
              </span>
              <span className="text-3xl md:text-4xl lg:text-5xl block text-[#e91e8c]" data-id="element-926">
                IN 60 SECONDS
              </span>
            </h1>
            <p className="text-gray-600 font-medium max-w-2xl mx-auto text-lg" data-id="element-927">
              Fill in the questions below and we'll call you back instantly with
              a tailored quote.
            </p>
          </motion.div>
        </section>

        {/* Form Section */}
        <section className="w-full bg-[#f8f9fa] py-12 md:py-16 px-4 md:px-8" id="quote-form" data-id="element-928">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="max-w-4xl w-full mx-auto" data-id="element-929">
            <form className="space-y-8" onSubmit={e => e.preventDefault()} data-id="element-930">
              {/* STEP 1 */}
              <div className={cardClass} data-id="element-931">
                <h3 className={stepHeadingClass} data-id="element-932">
                  <span className="text-[#e91e8c] mr-2" data-id="element-933">STEP 1:</span> Tell us
                  about your commercial space
                </h3>
                <div className="space-y-6 mt-8" data-id="element-934">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-935">
                    <div className="space-y-2" data-id="element-936">
                      <label className={labelClass} data-id="element-937">
                        Primary use of the space
                      </label>
                      <div className="relative" data-id="element-938">
                        <select value={primaryUse} onChange={e => setPrimaryUse(e.target.value)} className={selectClass} data-id="element-939">
                          <option value="" disabled data-id="element-940">
                            Select space type
                          </option>
                          <option value="office" data-id="element-941">Office / Corporate</option>
                          <option value="retail" data-id="element-942">Retail Store</option>
                          <option value="medical" data-id="element-943">Medical / Healthcare</option>
                          <option value="educational" data-id="element-944">
                            Educational Facility
                          </option>
                          <option value="industrial" data-id="element-945">
                            Industrial / Warehouse
                          </option>
                          <option value="other" data-id="element-946">Other</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-947" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-948">
                      <label className={labelClass} data-id="element-949">Total Square Footage</label>
                      <input type="text" value={sqFootage} onChange={e => setSqFootage(e.target.value)} placeholder="Enter square footage" className={inputClass} data-id="element-950" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-951">
                    <div className="space-y-2" data-id="element-952">
                      <label className={labelClass} data-id="element-953">Height of Ceilings</label>
                      <input type="text" value={ceilings} onChange={e => setCeilings(e.target.value)} placeholder="Average height of ceiling" className={inputClass} data-id="element-954" />
                    </div>
                    <div className="space-y-2" data-id="element-955">
                      <label className={labelClass} data-id="element-956">
                        Approx. Total # of persons in the space
                      </label>
                      <div className="relative" data-id="element-957">
                        <select value={persons} onChange={e => setPersons(e.target.value)} className={selectClass} data-id="element-958">
                          <option value="" disabled data-id="element-959">
                            Select range
                          </option>
                          <option value="1-10" data-id="element-960">1-10</option>
                          <option value="11-25" data-id="element-961">11-25</option>
                          <option value="26-50" data-id="element-962">26-50</option>
                          <option value="51-100" data-id="element-963">51-100</option>
                          <option value="100+" data-id="element-964">100+</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-965" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-966">
                    <label className={labelClass} data-id="element-967">Cleaning frequency</label>
                    <div className="relative" data-id="element-968">
                      <select value={frequency} onChange={e => setFrequency(e.target.value)} className={selectClass} data-id="element-969">
                        <option value="" disabled data-id="element-970">
                          Select frequency
                        </option>
                        <option value="daily" data-id="element-971">Daily</option>
                        <option value="daily-weekends" data-id="element-972">
                          Daily Including weekends
                        </option>
                        <option value="weekly" data-id="element-973">Weekly</option>
                        <option value="biweekly" data-id="element-974">Bi-weekly</option>
                        <option value="monthly" data-id="element-975">Monthly</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-976" />
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-977">
                    <label className={labelClass} data-id="element-978">
                      What time of day would you like the space cleaned?
                    </label>
                    <div className="flex flex-wrap gap-3" data-id="element-979">
                      {['During Regular Business hours', 'Outside Regular Business hours', 'Overnight (11 pm to 7 am)'].map(time => <button key={time} type="button" onClick={() => setTimeOfDay(time)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${timeOfDay === time ? 'bg-[#e91e8c] border-[#e91e8c] text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-980">
                          {time}
                        </button>)}
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 pt-2" data-id="element-981">
                    <label className={`${labelClass} md:flex-1`} data-id="element-982">
                      Has anyone in the space contracted COVID-19 in the last
                      month?
                    </label>
                    <div className="flex gap-3" data-id="element-983">
                      <button type="button" onClick={() => setCovid(true)} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all border ${covid === true ? 'bg-[#e91e8c] border-[#e91e8c] text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-984">
                        Yes
                      </button>
                      <button type="button" onClick={() => setCovid(false)} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all border ${covid === false ? 'bg-[#1a2744] border-[#1a2744] text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-[#1a2744] hover:text-[#1a2744]'}`} data-id="element-985">
                        No
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 2 */}
              <div className={cardClass} data-id="element-986">
                <h3 className={stepHeadingClass} data-id="element-987">
                  <span className="text-[#e91e8c] mr-2" data-id="element-988">STEP 2:</span> Select
                  Space Types
                </h3>
                <p className="text-sm text-gray-500 mt-3" data-id="element-989">
                  Select the quantity for each space type. This helps us provide
                  the most accurate estimate.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-id="element-990">
                  {spaceTypes.map(({
                  key,
                  label,
                  icon: IconComp
                }) => <div key={key} className="border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center gap-3 hover:border-[#5bbcd5] transition-colors bg-[#f8f9fa]" data-id="element-991">
                      <div className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center text-[#1a2744]" data-id="element-992">
                        <IconComp className="w-7 h-7" data-id="element-993" />
                      </div>
                      <span className="font-medium text-[#1a2744] text-sm" data-id="element-994">
                        {label}
                      </span>
                      <div className="flex items-center gap-4 mt-1" data-id="element-995">
                        <button type="button" onClick={() => updateSpace(key, false)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-996">
                          <Minus className="w-4 h-4" data-id="element-997" />
                        </button>
                        <span className="w-6 text-xl font-semibold text-[#1a2744] text-center" data-id="element-998">
                          {spaces[key]}
                        </span>
                        <button type="button" onClick={() => updateSpace(key, true)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-999">
                          <Plus className="w-4 h-4" data-id="element-1000" />
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* STEP 3 */}
              <div className={cardClass} data-id="element-1001">
                <h3 className={stepHeadingClass} data-id="element-1002">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1003">STEP 3:</span> Type of
                  Flooring
                </h3>
                <p className="text-sm text-gray-500 mt-3" data-id="element-1004">
                  Provide your best assessment of the overall flooring type in
                  the area you would like cleaned.
                </p>
                <div className="space-y-6 mt-8" data-id="element-1005">
                  {flooringTypes.map(({
                  key,
                  label
                }) => <div key={key} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-id="element-1006">
                      <label className={`${labelClass} md:flex-1`} data-id="element-1007">
                        {label}
                      </label>
                      <div className="relative md:w-48" data-id="element-1008">
                        <select value={flooring[key]} onChange={e => setFlooring({
                      ...flooring,
                      [key]: e.target.value
                    })} className={selectClass} data-id="element-1009">
                          <option value="" disabled data-id="element-1010">
                            Select %
                          </option>
                          {percentages.map(p => <option key={p} value={p} data-id="element-1011">
                              {p}
                            </option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1012" />
                      </div>
                    </div>)}
                  <div className="space-y-2 pt-2" data-id="element-1013">
                    <label className={labelClass} data-id="element-1014">
                      Any other information regarding the floor space?
                    </label>
                    <textarea value={flooring.other} onChange={e => setFlooring({
                    ...flooring,
                    other: e.target.value
                  })} placeholder="Please add any other information you feel we should know." rows={3} className="w-full border border-gray-200 rounded-lg p-4 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all resize-none placeholder-gray-400" data-id="element-1015" />
                  </div>
                </div>
              </div>

              {/* STEP 4 */}
              <div className={cardClass} data-id="element-1016">
                <h3 className={stepHeadingClass} data-id="element-1017">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1018">STEP 4:</span> Contact
                  Information
                </h3>
                <p className="text-sm text-gray-500 mt-3" data-id="element-1019">
                  You will instantly receive an email summary. We will also call
                  you within 10 minutes to provide your quote.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1020">
                  <div className="space-y-2" data-id="element-1021">
                    <label className={labelClass} data-id="element-1022">First Name</label>
                    <input type="text" name="firstName" value={contact.firstName} onChange={handleContactChange} placeholder="First Name" className={inputClass} data-id="element-1023" />
                  </div>
                  <div className="space-y-2" data-id="element-1024">
                    <label className={labelClass} data-id="element-1025">Last Name</label>
                    <input type="text" name="lastName" value={contact.lastName} onChange={handleContactChange} placeholder="Last Name" className={inputClass} data-id="element-1026" />
                  </div>
                  <div className="space-y-2" data-id="element-1027">
                    <label className={labelClass} data-id="element-1028">Email Address</label>
                    <input type="email" name="email" value={contact.email} onChange={handleContactChange} placeholder="Email Address" className={inputClass} data-id="element-1029" />
                  </div>
                  <div className="space-y-2" data-id="element-1030">
                    <label className={labelClass} data-id="element-1031">Phone Number</label>
                    <input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} placeholder="Phone Number" className={inputClass} data-id="element-1032" />
                  </div>
                </div>
              </div>

              {/* STEP 5 */}
              <div className={cardClass} data-id="element-1033">
                <h3 className={stepHeadingClass} data-id="element-1034">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1035">STEP 5:</span> Location
                  of the Commercial Space
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1036">
                  <div className="space-y-2" data-id="element-1037">
                    <label className={labelClass} data-id="element-1038">Street Address</label>
                    <input type="text" name="location" value={contact.location} onChange={handleContactChange} placeholder="Enter a location / Street Address" className={inputClass} data-id="element-1039" />
                  </div>
                  <div className="space-y-2" data-id="element-1040">
                    <label className={labelClass} data-id="element-1041">Unit/Apt/Suite #</label>
                    <input type="text" name="unit" value={contact.unit} onChange={handleContactChange} placeholder="Unit/Apt/Suite #" className={inputClass} data-id="element-1042" />
                  </div>
                  <div className="space-y-2" data-id="element-1043">
                    <label className={labelClass} data-id="element-1044">City</label>
                    <input type="text" name="city" value={contact.city} onChange={handleContactChange} placeholder="City" className={inputClass} data-id="element-1045" />
                  </div>
                  <div className="space-y-2" data-id="element-1046">
                    <label className={labelClass} data-id="element-1047">Postal Code</label>
                    <input type="text" name="postalCode" value={contact.postalCode} onChange={handleContactChange} placeholder="Postal Code" className={inputClass} data-id="element-1048" />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-center pt-4 pb-4" data-id="element-1049">
                <button type="submit" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-12 py-4 rounded-full font-semibold tracking-wider text-lg transition-colors shadow-lg hover:shadow-xl" data-id="element-1050">
                  REQUEST A QUOTE
                </button>
              </div>
            </form>
          </motion.div>
        </section>

        {/* Explore Our Services */}
        <section className="w-full bg-white py-20 px-4 md:px-12 border-t border-gray-200" data-id="element-1051">
          <div className="max-w-7xl mx-auto" data-id="element-1052">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16" data-id="element-1053">
              <h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-[#1a2744] tracking-wide" data-id="element-1054">
                EXPLORE OUR <span className="text-[#e91e8c]" data-id="element-1055">SERVICES</span>
              </h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto" data-id="element-1056">
                Discover our full range of professional cleaning solutions
                tailored to your needs.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1057">
              {[{
              title: 'Basic Home Cleaning',
              desc: 'Regular maintenance to keep your home spotless.',
              link: '/basic-home-cleaning',
              icon: Home
            }, {
              title: 'Spring / Deep Cleaning',
              desc: 'Thorough, top-to-bottom detail cleaning.',
              link: '/spring-deep-cleaning',
              icon: Sparkles
            }, {
              title: 'Commercial Cleaning',
              desc: 'Professional cleaning for your business.',
              link: '/commercial-cleaning',
              icon: Building2
            }, {
              title: 'Contact Us',
              desc: 'Get in touch for custom requests.',
              link: '/contact-us',
              icon: Phone
            }].map((service, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.1
            }} data-id="element-1058">
                  <Link to={service.link} className="group block h-full bg-[#f8f9fa] rounded-xl p-8 hover:shadow-md transition-all border border-gray-100 hover:border-[#5bbcd5]" data-id="element-1059">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-6 group-hover:bg-[#5bbcd5]/10 transition-colors" data-id="element-1060">
                      <service.icon className="w-6 h-6 text-[#1a2744] group-hover:text-[#5bbcd5] transition-colors" data-id="element-1061" />
                    </div>
                    <h3 className="font-bebas text-2xl text-[#1a2744] mb-3 tracking-wide group-hover:text-[#e91e8c] transition-colors" data-id="element-1062">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-6 line-clamp-2" data-id="element-1063">
                      {service.desc}
                    </p>
                    <div className="flex items-center text-[#5bbcd5] font-medium text-sm mt-auto" data-id="element-1064">
                      Learn more
                      <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-1065" />
                    </div>
                  </Link>
                </motion.div>)}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1066" />
    </div>;
}